"use client";

import React, { useState, useEffect } from "react";
import { 
  Utensils, 
  Wine, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Search, 
  Filter, 
  RotateCcw, 
  Sparkles, 
  Calendar,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import RestaurantKanban from "./RestaurantKanban";
import RestaurantStageModal from "./RestaurantStageModal";
import RestaurantDetailModal from "./RestaurantDetailModal";
import RestaurantMarketingTab from "./RestaurantMarketingTab";
import { RestaurantReservation } from "@/lib/demos/restaurantDb";

interface RestaurantCrmDashboardProps {
  currency?: "INR" | "USD";
}

export default function RestaurantCrmDashboard({ currency = "INR" }: RestaurantCrmDashboardProps) {
  const [activeTab, setActiveTab] = useState<"reservations" | "marketing">("marketing");
  const [reservations, setReservations] = useState<RestaurantReservation[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [salonFilter, setSalonFilter] = useState("ALL");

  // Modals state
  const [selectedReservation, setSelectedReservation] = useState<RestaurantReservation | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [pendingStageChange, setPendingStageChange] = useState<{
    reservation: RestaurantReservation;
    targetStage: "Table Reserved" | "Menu Curated" | "Billed & Paid" | "Cancelled";
  } | null>(null);

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/demos/restaurant/reservations");
      const data = await res.json();
      if (data.success) {
        setReservations(data.reservations);
        setStats(data.stats);
      }
    } catch (err) {
      console.error("Failed to load reservations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const handleSelectReservation = (res: RestaurantReservation) => {
    setSelectedReservation(res);
    setIsDetailOpen(true);
  };

  const handleUpdateStage = async (id: number, newStage: RestaurantReservation["stage"]) => {
    const target = reservations.find((r) => r.id === id);
    if (!target || target.stage === newStage) return;

    // Check if moving to a stage requiring modal inputs
    if (
      newStage === "Table Reserved" ||
      newStage === "Menu Curated" ||
      newStage === "Billed & Paid" ||
      newStage === "Cancelled"
    ) {
      setPendingStageChange({
        reservation: target,
        targetStage: newStage,
      });
      return;
    }

    // Direct transition (e.g. Seated & Dining or New Requests)
    try {
      const res = await fetch(`/api/demos/restaurant/reservations/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: newStage }),
      });
      const data = await res.json();
      if (data.success) {
        setReservations((prev) =>
          prev.map((r) => (r.id === id ? data.reservation : r))
        );
        showToast(data.automationMessage || `Reservation moved to ${newStage}`);
      }
    } catch (err) {
      console.error("Failed to update reservation stage:", err);
    }
  };

  const handleConfirmStageModal = async (opts: any) => {
    if (!pendingStageChange) return;
    const { reservation } = pendingStageChange;

    try {
      const res = await fetch(`/api/demos/restaurant/reservations/${reservation.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts),
      });
      const data = await res.json();
      if (data.success) {
        setReservations((prev) =>
          prev.map((r) => (r.id === reservation.id ? data.reservation : r))
        );
        showToast(data.automationMessage || `Reservation advanced to ${opts.stage}`);
      }
    } catch (err) {
      console.error("Failed to complete stage transition:", err);
    } finally {
      setPendingStageChange(null);
    }
  };

  const handleDeleteReservation = async (id: number) => {
    try {
      const res = await fetch(`/api/demos/restaurant/reservations/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setReservations((prev) => prev.filter((r) => r.id !== id));
        showToast("Reservation removed from floor plan");
      }
    } catch (err) {
      console.error("Failed to delete reservation:", err);
    }
  };

  // Filtered reservations
  const filteredReservations = reservations.filter((r) => {
    const matchesSearch =
      r.guest_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.phone && r.phone.includes(searchQuery)) ||
      r.tasting_menu.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSalon = salonFilter === "ALL" || r.dining_area.includes(salonFilter);

    return matchesSearch && matchesSalon;
  });

  const sym = currency === "USD" ? "$" : "₹";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1600px] mx-auto min-h-screen">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-stone-900 border border-amber-500/40 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-3 duration-200">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
          <span className="text-xs font-semibold">{toastMessage}</span>
        </div>
      )}

      {/* CRM Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-stone-800">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[11px] font-bold text-amber-400 uppercase tracking-wider">
              Hospitality Automation Demo
            </span>
            <span className="text-xs text-stone-400 font-medium">• L'Aura Gastronomy</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
            Floor & Table Reservation CRM
          </h1>
          <p className="text-xs sm:text-sm text-stone-400">
            Real-time table allocation, sommelier curation, and automated invoice dispatches.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchReservations}
            className="p-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-300 border border-stone-800 text-xs font-semibold transition-colors flex items-center gap-1.5"
            title="Refresh floor data"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-amber-400" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-2 border-b border-stone-800 pb-3">
        <button
          onClick={() => setActiveTab("marketing")}
          className={`py-2 px-4 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === "marketing"
              ? "bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20"
              : "bg-stone-900 text-stone-400 hover:text-white hover:bg-stone-800"
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>VIP Diners & WhatsApp Marketing</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-bold ${
            activeTab === "marketing" ? "bg-amber-950/20 text-slate-950" : "bg-amber-500/20 text-amber-400"
          }`}>
            QR Automation
          </span>
        </button>

        <button
          onClick={() => setActiveTab("reservations")}
          className={`py-2 px-4 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === "reservations"
              ? "bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20"
              : "bg-stone-900 text-stone-400 hover:text-white hover:bg-stone-800"
          }`}
        >
          <Utensils className="w-3.5 h-3.5" />
          <span>Table Reservations (Floor Plan)</span>
        </button>
      </div>

      {activeTab === "marketing" ? (
        <RestaurantMarketingTab restaurantName="The Smoke Co." />
      ) : (
        <>
          {/* KPI Stats Header */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Total Reservations */}
            <div className="p-4 rounded-2xl bg-stone-900/70 border border-stone-800/90 space-y-2">
              <div className="flex items-center justify-between text-stone-400">
                <span className="text-xs font-semibold">Active Bookings</span>
                <Utensils className="w-4 h-4 text-amber-400" />
              </div>
              <div className="text-2xl font-black text-white">
                {stats?.totalReservations ?? reservations.length}
              </div>
              <span className="text-[11px] text-stone-400 block">
                {stats?.confirmedCount ?? 7} Confirmed Tables
              </span>
            </div>

            {/* Card 2: Total Diners / Covers */}
            <div className="p-4 rounded-2xl bg-stone-900/70 border border-stone-800/90 space-y-2">
              <div className="flex items-center justify-between text-stone-400">
                <span className="text-xs font-semibold">Tonight's Covers</span>
                <Users className="w-4 h-4 text-indigo-400" />
              </div>
              <div className="text-2xl font-black text-white">
                {stats?.totalGuests ?? 38} Guests
              </div>
              <span className="text-[11px] text-indigo-300 block">
                Across 6 Service Sittings
              </span>
            </div>

            {/* Card 3: Table Occupancy */}
            <div className="p-4 rounded-2xl bg-stone-900/70 border border-stone-800/90 space-y-2">
              <div className="flex items-center justify-between text-stone-400">
                <span className="text-xs font-semibold">Floor Occupancy</span>
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="text-2xl font-black text-emerald-400">
                {stats?.occupancyRate ?? 85}%
              </div>
              <span className="text-[11px] text-stone-400 block">
                Peak Sitting (08:30 PM) at Capacity
              </span>
            </div>

            {/* Card 4: Booked Gastronomy Revenue */}
            <div className="p-4 rounded-2xl bg-stone-900/70 border border-stone-800/90 space-y-2">
              <div className="flex items-center justify-between text-stone-400">
                <span className="text-xs font-semibold">Booked Revenue</span>
                <Wine className="w-4 h-4 text-amber-400" />
              </div>
              <div className="text-2xl font-black text-amber-400">
                {sym}{(stats?.totalRevenue ?? 420000).toLocaleString()}
              </div>
              <span className="text-[11px] text-stone-400 block">
                Tasting Menus & Cellar Pairings
              </span>
            </div>
          </div>

          {/* Filter & Search Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-stone-900/40 p-3 rounded-2xl border border-stone-800/80">
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 text-stone-500 absolute left-3.5 top-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by guest, email, tasting menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-stone-950 border border-stone-800 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder:text-stone-500 focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Filter className="w-3.5 h-3.5 text-stone-400 shrink-0 hidden sm:block" />
              <select
                value={salonFilter}
                onChange={(e) => setSalonFilter(e.target.value)}
                className="w-full sm:w-auto bg-stone-950 border border-stone-800 rounded-xl px-3 py-2 text-xs text-stone-300 focus:outline-none focus:border-amber-500 transition-colors"
              >
                <option value="ALL">All Dining Salons</option>
                <option value="Main Dining">Main Dining Room</option>
                <option value="Wine Vault">Sommelier's Wine Vault</option>
                <option value="Chef's Counter">Executive Chef's Counter</option>
                <option value="Skyline Glass">Skyline Conservatory</option>
              </select>
            </div>
          </div>

          {/* Kanban Board */}
          <RestaurantKanban
            reservations={filteredReservations}
            onSelectReservation={handleSelectReservation}
            onUpdateStage={handleUpdateStage}
            currency={currency}
          />
        </>
      )}

      {/* Stage Transition Modal */}
      {pendingStageChange && (
        <RestaurantStageModal
          reservation={pendingStageChange.reservation}
          targetStage={pendingStageChange.targetStage}
          isOpen={true}
          onClose={() => setPendingStageChange(null)}
          onConfirm={handleConfirmStageModal}
        />
      )}

      {/* Detail Modal */}
      <RestaurantDetailModal
        reservation={selectedReservation}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedReservation(null);
        }}
        onDelete={handleDeleteReservation}
      />
    </div>
  );
}
